import classNames from 'classnames'
import { InView, useInView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { Container } from '../Container'
import { BannerHero } from './BannerHero'
import { CardBanner } from './CardBanner'
import type { NavigateByCategoriesProps } from '../types/navigate-by-categories'
import { useNavigateByCategories } from './useNavigateByCategories'

export function NavigateByCategories(data: NavigateByCategoriesProps) {
  const { pageData } = useGlobalContext()
  const { ref } = useInView({
    threshold: 0.7,
    onChange: (inView) => {
      if (!inView) {
        return
      }

      handleInview()
    },
  })

  const { validateProps } = useNavigateByCategories()

  const propsIsValid = validateProps(data)

  const {
    banner,
    categories,
    sessionTitle,
    enableSession,
    section,
    locationOnPage,
  } = data

  const handleInview = () => {
    if (window === null || !data?.categories || !data?.banner) {
      return
    }

    window.dataLayer = window?.dataLayer ?? []

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      ecommerce: {
        promoView: {
          promotions: [
            ...data.categories.map((category, index) => ({
              id: category.name,
              name: category.name,
              creative: category.image,
              position: `slot${index + 1}`,
            })),
            data.banner?.enable && {
              id: data.banner.sessionTitle,
              name: data.banner.sessionTitle,
              creative: data.banner.image,
              position: 'slot1',
            },
          ],
        },
      },
    })
  }

  const sendPromoFeatureViewedEvent = (
    item: { name: string },
    index?: number
  ) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: item?.name ?? 'N/A',
        index,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  if (!enableSession || !propsIsValid) {
    return <></>
  }

  return (
    <Container
      className="py-[40px] restructure-tablet:py-[48px] restructure-small-desktop:py-[60px] px-4"
      ref={ref}
    >
      <h2 className="mobile-heading-title3 tablet-mobile-heading-title3 desktop-heading-title3 text-restructure-primary mb-lg restructure-small-desktop:mb-8">
        {sessionTitle.text}
      </h2>
      <div className="flex flex-col-reverse restructure-tablet:flex-row gap-md restructure-small-desktop:gap-md ">
        <ul
          className={classNames(
            'flex flex-wrap restructure-maxMobile:gap-2 gap-lg w-full content-start restructure-tablet:w-1/2',
            { '!w-full': !banner.enable }
          )}
        >
          {categories?.map((item, index) => (
            <InView
              as="li"
              threshold={0.7}
              onChange={(inView) => {
                if (inView) {
                  sendPromoFeatureViewedEvent(item, index)
                }
              }}
              triggerOnce
              className="w-full h-fit restructure-small-desktop:w-[calc(100%/2-12px)] restructure-small-desktop:max-w-[302px]"
              key={item.name}
            >
              <CardBanner
                image={item.image}
                linkUrl={item.linkURL}
                name={item.name}
                position={index + 1}
                index={index}
                section={section}
                locationOnPage={locationOnPage}
              />
            </InView>
          ))}
        </ul>
        {banner?.enable && (
          <InView
            as="div"
            threshold={0.7}
            onChange={(inView) => {
              if (inView) {
                sendPromoFeatureViewedEvent({ name: banner.sessionTitle.text })
              }
            }}
            triggerOnce
            className="w-full restructure-tablet:w-1/2"
          >
            <BannerHero
              image={banner.image}
              title={banner.sessionTitle}
              button={banner.sessionButton}
              section={section}
              locationOnPage={locationOnPage}
            />
          </InView>
        )}
      </div>
    </Container>
  )
}
